import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Form,
	FormField,
	Header,
	Input,
	Select,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import useSingleApplication from '../../../../../hooks/query/useSingleApplication'
import usePlatforms from '../../../../../hooks/query/usePlatforms'
import useGcpCerts from '../../../../../hooks/query/useGcpCerts'
import useCreateNewGcpCert from '../../../../../hooks/mutate/useCreateNewGcpCert'
import { GcpCertRequest, getGcpCertApprovalObject } from '../../../../../types/gcpCert'
import type { Application } from '../../../../../types/applications'
import type { GcpCert } from '../../../../../types/gcpCert'

const CERTIFICATE_TYPE_OPTIONS = [
	{ label: 'SSL certificates/Classic Certificates', value: 'ssl_classic' },
	{ label: 'Self-managed certificates/certificatev2', value: 'self_managed_v2' }
]

const getDnsDomainFromProjectId = (projectId: string) => {
	if (!projectId) return ''
	const parts = projectId.split('-')
	const platformAlias = parts[2] || ''
	const env = parts[parts.length - 1] || ''
	const envPart = ['dev', 'stg', 'sbx'].includes(env) ? `-${env}` : ''
	return `${platformAlias}${envPart}.gcp.globetel.cloud.`
}

const getNetworkProjectFromSubnetwork = (subnetworkName: string): string | null => {
	const s = (subnetworkName || '').toLowerCase()
	if (s.includes('-sb-')) return 'glo-ntwksvcs-sb-vk81'
	if (s.includes('-st-')) return 'glo-ntwksvcs-st-vg21'
	if (s.includes('-d-')) return 'glo-ntwksvcs-d-vg11'
	if (s.includes('-p-')) return 'glo-ntwksvcs-p-vg01'
	return null
}

const resolvePlatformId = (application: Application & { projectId?: string }) => {
	const platformName = application?.details?.platformName
	if (
		typeof platformName === 'object' &&
		platformName !== null &&
		'value' in platformName &&
		platformName.value &&
		platformName.value !== 'custom'
	) {
		return String(platformName.value)
	}
	const platformId = application?.details?.platformID
	if (platformId && platformId !== 'custom') return String(platformId)
	return ''
}

const CERTIFICATE_VIEWER_DOMAIN = '@globe.com.ph'

const validateCertificateViewer = (value: string): string | null => {
	const trimmed = value.trim()
	if (!trimmed) return 'Certificate Viewer (distribution list) is required.'
	const items = trimmed.split(',').map((s) => s.trim()).filter(Boolean)
	if (items.length === 0) return 'Certificate Viewer (distribution list) is required.'
	const invalid = items.filter((item) => !item.toLowerCase().endsWith(CERTIFICATE_VIEWER_DOMAIN))
	if (invalid.length > 0) {
		return `Each value must be a distribution list (DL) ending with ${CERTIFICATE_VIEWER_DOMAIN}. Invalid: ${invalid.join(', ')}`
	}
	return null
}

const ApplicationGcpCertNew: FC = () => {
	const { id: applicationID } = useParams() as { id: string }
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const thisUserEmail = (authState?.idToken?.claims?.email as string) ?? ''

	const { data: application, isLoading: isAppLoading } = useSingleApplication(applicationID ?? '')
	const { data: platforms } = usePlatforms()
	const { data: existingCerts } = useGcpCerts()
	const { mutate: createCert, isLoading: isSendLoading } = useCreateNewGcpCert()

	const [certificateType, setCertificateType] = useState('')
	const [subnetworkName, setSubnetworkName] = useState('')
	const [networkProjectId, setNetworkProjectId] = useState('')
	const [certificateViewer, setCertificateViewer] = useState('')
	const [subDomains, setSubDomains] = useState<Array<{ id: string; value: string }>>([])
	const [subDomainInput, setSubDomainInput] = useState('')
	const [subDomainDuplicateError, setSubDomainDuplicateError] = useState('')

	const app = application as Application & { projectId?: string }
	const isApproved = app?.status === 'APPROVED'
	const projectId = app?.projectId ?? (app?.approval as any)?.details?.projectId ?? (app?.details as any)?.projectId ?? ''
	const applicationName = app?.details?.applicationName ?? ''
	const platformName = typeof app?.details?.platformName === 'object' && app?.details?.platformName !== null && 'label' in app.details.platformName
		? (app.details.platformName as { label: string }).label
		: String(app?.details?.platformName ?? '')
	const applicationDns = projectId ? getDnsDomainFromProjectId(projectId) : ''
	const applicationAlias = app?.details?.applicationAlias ?? ''
	const wbsCode = app?.details?.wbsCode?.trim() || 'N/A'
	const costCenter = app?.details?.costCenter?.trim() || 'N/A'
	const gcpProjectId = projectId || ''
	const environment = typeof app?.details?.environment === 'object' && app?.details?.environment !== null && 'value' in app.details.environment
		? (app.details.environment as { value: string }).value
		: String(app?.details?.environment ?? '')
	const applicationOwner = (app?.details as any)?.applicationOwner ?? (app?.details as any)?.applicationOwnerName ?? 'N/A'
	const applicationOwnerEmail = (app?.details as any)?.applicationOwnerEmail ?? 'N/A'
	const platformId = resolvePlatformId(app)

	const resolvedPlatformNetwork = useMemo(() => {
		if (!platformId || !platforms?.length) return null
		const platformData = platforms.find((platform) => platform.platformID === platformId) as
			| (typeof platforms[number] & { subnetwork?: string; networkProjectID?: string })
			| undefined
		if (!platformData || platformData.status !== 'APPROVED') return null

		const subnetwork = platformData.subnetwork?.trim() ?? ''
		const networkProject =
			platformData.networkProjectID?.trim() ||
			getNetworkProjectFromSubnetwork(subnetwork) ||
			''
		if (!subnetwork && !networkProject) return null

		return { subnetwork, networkProject }
	}, [platformId, platforms])

	// Sub-domains already used by existing certs for this project (prefix -> cert id).
	// Only consider non-REJECTED certs: same domain can be reused for rejected requests or for different applications.
	const existingSubDomainToCertId = useMemo(() => {
		const map: Record<string, string> = {}
		if (!projectId || !existingCerts?.length) return map
		existingCerts.forEach((cert: GcpCert) => {
			if (cert.status === 'REJECTED') return
			const certProjectId = (cert as any).projectID ?? cert.details?.projectId
			if (certProjectId !== projectId) return
			const prefixes = cert.details?.dnsPrefixes ?? []
			prefixes.forEach((p: string) => {
				const key = (p || '').trim().toLowerCase()
				if (key && !map[key]) map[key] = cert.gcpCertID
			})
		})
		return map
	}, [projectId, existingCerts])

	useEffect(() => {
		if (!isApproved && !isAppLoading && app?.status) {
			navigate(`/applications/${applicationID}`, { replace: true })
		}
	}, [isApproved, isAppLoading, app?.status, navigate, applicationID])

	useEffect(() => {
		if (!resolvedPlatformNetwork) return
		if (resolvedPlatformNetwork.subnetwork) {
			setSubnetworkName((prev) => prev || resolvedPlatformNetwork.subnetwork)
		}
		if (resolvedPlatformNetwork.networkProject) {
			setNetworkProjectId((prev) => prev || resolvedPlatformNetwork.networkProject)
		}
	}, [resolvedPlatformNetwork])

	const networkProjectFromSubnetwork = getNetworkProjectFromSubnetwork(subnetworkName)
	useEffect(() => {
		if (networkProjectFromSubnetwork) setNetworkProjectId(networkProjectFromSubnetwork)
	}, [networkProjectFromSubnetwork])

	const certificateViewerError = useMemo(
		() => validateCertificateViewer(certificateViewer),
		[certificateViewer]
	)

	const isSubmitDisabled = useMemo(() => {
		if (isSendLoading) return true
		if (!certificateType) return true
		if (subDomains.length === 0) return true
		if (!subnetworkName.trim()) return true
		if (!networkProjectId.trim()) return true
		if (certificateViewerError) return true
		if (subDomainDuplicateError) return true
		if (!gcpProjectId.trim()) return true
		if (!applicationDns.trim()) return true
		if (!applicationName.trim()) return true
		if (!platformName.trim()) return true
		if (!environment.trim()) return true
		return false
	}, [
		isSendLoading,
		certificateType,
		subDomains.length,
		subnetworkName,
		networkProjectId,
		certificateViewerError,
		subDomainDuplicateError,
		gcpProjectId,
		applicationDns,
		applicationName,
		platformName,
		environment
	])

	const handleAddSubDomain = () => {
		const v = subDomainInput.trim()
		if (!v) {
			setSubDomainDuplicateError('')
			return
		}
		const normalized = v.toLowerCase()
		// 1) Already in current form list
		if (subDomains.some((s) => s.value.toLowerCase() === normalized)) {
			setSubDomainDuplicateError('This sub-domain has already been added.')
			return
		}
		// 2) Already used by an existing certificate for this project
		const existingCertId = existingSubDomainToCertId[normalized]
		if (existingCertId) {
			setSubDomainDuplicateError(`This sub-domain is already used by an existing certificate (${existingCertId}).`)
			return
		}
		setSubDomainDuplicateError('')
		setSubDomains((prev) => [...prev, { id: `sd-${Date.now()}`, value: v }])
		setSubDomainInput('')
	}

	const handleRemoveSubDomain = (id: string) => {
		setSubDomains((prev) => prev.filter((s) => s.id !== id))
	}

	const handleSubmit = () => {
		if (isSubmitDisabled) return
		const certViewerError = validateCertificateViewer(certificateViewer)
		if (certViewerError) return
		// Dedupe sub-domains (case-insensitive), preserve order
		const seen = new Set<string>()
		const dnsPrefixes = subDomains
			.map((s) => s.value.trim())
			.filter((v) => {
				if (!v) return false
				const key = v.toLowerCase()
				if (seen.has(key)) return false
				seen.add(key)
				return true
			})
		// Block submit if any sub-domain is already used by an existing cert
		for (const prefix of dnsPrefixes) {
			const existingCertId = existingSubDomainToCertId[prefix.toLowerCase()]
			if (existingCertId) {
				setSubDomainDuplicateError(`Cannot submit: sub-domain "${prefix}" is already used by an existing certificate (${existingCertId}).`)
				return
			}
		}
		setSubDomainDuplicateError('')
		const payload: GcpCertRequest = {
			status: 'PENDING_APPROVAL',
			requestorID: thisUserEmail,
			action: 'CREATE',
			details: {
				applicationAlias,
				...(applicationName && { applicationName }),
				environment,
				platformName,
				projectId: gcpProjectId,
				applicationOwner: applicationOwner === 'N/A' ? '' : applicationOwner,
				applicationOwnerEmail: applicationOwnerEmail === 'N/A' ? '' : applicationOwnerEmail,
				wbsCode: wbsCode === 'N/A' ? '' : wbsCode,
				costCenter: costCenter === 'N/A' ? '' : costCenter,
				dnsDomainName: applicationDns,
				dnsPrefixes,
				...(certificateType && { certificateType }),
				subnetworkName: subnetworkName.trim() || undefined,
				networkProjectId: networkProjectId.trim() || undefined,
				applicationID: applicationID || undefined,
				certificateViewer: certificateViewer.trim()
			},
			approvalStatus: getGcpCertApprovalObject(['GCP_TRIBE', 'APPLICATION_OWNER'])
		}
		createCert(payload, {
			onSuccess: () => {
				navigate('/gcpCerts')
			}
		})
	}

	const handleCancel = () => {
		navigate('/applications')
	}

	if (isAppLoading || !application) return <LoadingSpinner />
	if (!isApproved) return null

	return (
		<Form
			header={
				<Header variant="h1">
					Application Certificate
				</Header>
			}
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="primary" onClick={handleSubmit} loading={isSendLoading} disabled={isSubmitDisabled}>
						Submit
					</Button>
					<Button variant="link" onClick={handleCancel}>
						Cancel
					</Button>
				</SpaceBetween>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<Container header={<Header variant="h2">Application Certificate</Header>}>
					<ColumnLayout columns={2} variant="text-grid">
						<SpaceBetween direction="vertical" size="m">
							<FormField label="Application name">
								<Input value={applicationName} readOnly disabled />
							</FormField>
							<FormField label="Application DNS">
								<Input value={applicationDns} readOnly disabled />
							</FormField>
							<FormField
								label="SUB_DOMAINS"
								description="Add one or more sub-domains."
								constraintText="Required"
								errorText={subDomainDuplicateError || undefined}
							>
								<SpaceBetween direction="vertical" size="xs">
									<SpaceBetween direction="horizontal" size="xs">
										<Input
											value={subDomainInput}
											onChange={({ detail }) => {
												setSubDomainInput(detail.value)
												if (subDomainDuplicateError) setSubDomainDuplicateError('')
											}}
											placeholder="Sub-domain"
											invalid={!!subDomainDuplicateError}
											onKeyDown={(e) => {
												if (e.detail.key === 'Enter') {
													handleAddSubDomain()
												}
											}}
										/>
										<Button onClick={handleAddSubDomain}>Add sub-domain</Button>
									</SpaceBetween>
									{subDomains.length > 0 && (
										<SpaceBetween direction="vertical" size="xs">
											{subDomains.map((sd) => (
												<div
													key={sd.id}
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 8,
														padding: '6px 8px',
														border: '1px solid #ddd',
														borderRadius: 4,
														background: 'var(--color-background-container)'
													}}
												>
													<span style={{ flex: 1 }}>{sd.value}</span>
													<Button
														variant="inline-icon"
														iconName="remove"
														ariaLabel="Remove"
														onClick={() => handleRemoveSubDomain(sd.id)}
													/>
												</div>
											))}
										</SpaceBetween>
									)}
								</SpaceBetween>
							</FormField>
						</SpaceBetween>
						<SpaceBetween direction="vertical" size="m">
							<FormField label="Certificate Type" constraintText="Required">
								<Select
									selectedOption={certificateType ? { label: CERTIFICATE_TYPE_OPTIONS.find((o) => o.value === certificateType)?.label ?? certificateType, value: certificateType } : null}
									onChange={({ detail }) => setCertificateType(detail.selectedOption?.value ?? '')}
									options={CERTIFICATE_TYPE_OPTIONS}
									placeholder="Select Certificate Type"
									invalid={!certificateType}
								/>
							</FormField>
							<FormField label="Platform Name">
								<Input value={platformName} readOnly disabled />
							</FormField>
							<FormField label="Subnetwork name">
								<Input
									value={subnetworkName}
									onChange={({ detail }) => setSubnetworkName(detail.value)}
									placeholder="Enter subnetwork name"
									readOnly={!!resolvedPlatformNetwork?.subnetwork}
									disabled={!!resolvedPlatformNetwork?.subnetwork}
								/>
							</FormField>
							<FormField
								label="Network project ID"
							>
								<Input
									value={networkProjectId}
									placeholder="Enter network project ID"
									disabled
									readOnly
								/>
							</FormField>
							<FormField
								label="Certificate Viewer"
								description="Distribution list (DL) as comma-separated values. Each value must end with @globe.com.ph"
								errorText={certificateViewerError ?? undefined}
								constraintText="Required"
							>
								<Input
									value={certificateViewer}
									onChange={({ detail }) => setCertificateViewer(detail.value)}
									invalid={!!certificateViewerError}
								/>
							</FormField>
						</SpaceBetween>
					</ColumnLayout>
				</Container>

				<Container
					header={
						<Header variant="h2">
							Tag Details
						</Header>
					}
				>
					<ColumnLayout columns={3} variant="text-grid">
						<FormField label="Application Owner">
							<Input value={applicationOwner} readOnly disabled />
						</FormField>
						<FormField label="Application Owner Mail">
							<Input value={applicationOwnerEmail} readOnly disabled />
						</FormField>
						<FormField label="WBS_CODE">
							<Input value={wbsCode} readOnly disabled />
						</FormField>
						<FormField label="Application Name">
							<Input value={applicationName} readOnly disabled />
						</FormField>
						<FormField label="GCP_PROJECT_ID">
							<Input value={gcpProjectId} readOnly disabled />
						</FormField>
						<FormField label="COST_Center">
							<Input value={costCenter} readOnly disabled />
						</FormField>
						<FormField label="ENVIRONMENT">
							<Input value={environment} readOnly disabled />
						</FormField>
					</ColumnLayout>
				</Container>
			</SpaceBetween>
		</Form>
	)
}

export default ApplicationGcpCertNew
